import { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { useToasterStore } from 'react-hot-toast';

const SCROLL_TOP_THRESHOLD_PX = 16;
const TOAST_CLEAR_DELAY_MS = 1000;

type Params = {
  posts: Array<{ id: string }>;
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

export const useFeedNewPostsIndicator = ({ posts }: Params) => {
  const [hasNewPosts, setHasNewPosts] = useState(false);
  const lastSeenTopPostIdRef = useRef<string | null>(null);
  const previousPostsLengthRef = useRef(0);
  const lastToastHandledRef = useRef<string | null>(null);
  const { toasts } = useToasterStore();

  const markCurrentTopSeen = useCallback(() => {
    if (!_.isEmpty(posts)) {
      lastSeenTopPostIdRef.current = posts[0].id;
    }
  }, [posts]);

  const onLoadNewPosts = useCallback(() => {
    markCurrentTopSeen();
    setHasNewPosts(false);
    scrollToTop();
  }, [markCurrentTopSeen]);

  useEffect(() => {
    if (_.isEmpty(posts)) {
      previousPostsLengthRef.current = 0;
      return;
    }
    const topPostId = posts[0].id;
    if (lastSeenTopPostIdRef.current === null) {
      lastSeenTopPostIdRef.current = topPostId;
      previousPostsLengthRef.current = posts.length;
      return;
    }
    const idChanged = topPostId !== lastSeenTopPostIdRef.current;
    const lengthGrew = posts.length > previousPostsLengthRef.current;
    previousPostsLengthRef.current = posts.length;
    setHasNewPosts(idChanged && lengthGrew);
  }, [posts]);

  useEffect(() => {
    if (!hasNewPosts) return;
    const handleWindowScroll = () => {
      if (window.scrollY <= SCROLL_TOP_THRESHOLD_PX) {
        markCurrentTopSeen();
        setHasNewPosts(false);
      }
    };
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [hasNewPosts, markCurrentTopSeen]);

  useEffect(() => {
    if (!hasNewPosts) return;
    const activeToast = toasts.find((t) => t.visible);
    if (!lastToastHandledRef.current && activeToast) {
      lastToastHandledRef.current = activeToast.id;
    }
    if (!lastToastHandledRef.current) return;
    const trackedToast = toasts.find(
      (t) => t.id === lastToastHandledRef.current,
    );
    if (trackedToast?.visible) return;
    const timer = setTimeout(() => {
      onLoadNewPosts();
      lastToastHandledRef.current = null;
    }, TOAST_CLEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hasNewPosts, toasts, onLoadNewPosts]);

  return { hasNewPosts, onLoadNewPosts, scrollToTop };
};
